import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, MapPin, Sparkles, Heart, Cake } from 'lucide-react';

const EventTypes = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const eventTypes = [
    {
      id: 'murder-mystery',
      icon: <Sparkles size={48} />,
      title: 'Murder Mystery Dinners',
      tagline: 'An Interactive Evening of Intrigue',
      color: 'from-[#667eea] to-[#764ba2]',
      image: 'https://images.pexels.com/photos/787961/pexels-photo-787961.jpeg?w=800&q=80',
      description: 'Step into a world of mystery and suspense with our immersive Murder Mystery Dinner experiences. Perfect for corporate team building, private parties, or special celebrations.',
      features: [
        'Professional actors guide the interactive storyline',
        'Multiple themed scenarios available (1920s, Hollywood, Masquerade, etc.)',
        'Delicious 3-course dinner served during the show',
        'All guests become suspects and detectives',
        'Prizes for those who solve the mystery',
        'Customizable themes for your group',
        'Perfect for 20-200 guests'
      ],
      packages: [
        { name: 'Basic Package', price: '$65/person', includes: '3-course dinner, mystery experience, props' },
        { name: 'Premium Package', price: '$85/person', includes: 'Everything in Basic + cocktails, costume accessories, professional photos' },
        { name: 'VIP Package', price: '$110/person', includes: 'Everything in Premium + champagne toast, special character roles, private detective briefings' }
      ]
    },
    {
      id: 'destination-birthday',
      icon: <MapPin size={48} />,
      title: 'Destination Birthday Parties',
      tagline: 'Celebrate in Style, Anywhere',
      color: 'from-[#f093fb] to-[#f5576c]',
      image: 'https://images.unsplash.com/photo-1758599669452-0cf5fba4eb97?w=800&q=80',
      description: 'Take your birthday celebration to the next level with our destination party planning. Whether it\'s a beach house, mountain resort, or exclusive venue, we bring the party to you.',
      features: [
        'Full-service planning for any location',
        'Coordination with venues, transportation, and accommodations',
        'Custom menu development based on location and theme',
        'On-site catering team and servers',
        'Complete setup and breakdown',
        'Entertainment coordination (DJs, bands, activities)',
        'Photography and videography services available',
        'Birthday cakes and dessert bars'
      ],
      packages: [
        { name: 'Local Destination', price: 'Starting at $3,500', includes: 'Within 50 miles, up to 50 guests' },
        { name: 'Regional Adventure', price: 'Starting at $7,500', includes: '50-200 miles, up to 100 guests' },
        { name: 'Ultimate Getaway', price: 'Starting at $15,000', includes: 'Anywhere in the region, unlimited guests, multi-day events' }
      ]
    },
    {
      id: 'one-of-a-kind',
      icon: <Sparkles size={48} />,
      title: 'One of a Kind Parties',
      tagline: 'Uniquely You, Exceptionally Memorable',
      color: 'from-[#4facfe] to-[#00f2fe]',
      image: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=800&q=80',
      description: 'Have a vision that\'s truly unique? Our specialty is bringing extraordinary ideas to life. From themed extravaganzas to avant-garde experiences, we create parties that become legendary.',
      features: [
        'Completely custom concept development',
        'Unlimited creativity in theme and execution',
        'Specialty cuisine from around the world',
        'Custom décor and atmosphere creation',
        'Unique entertainment (fire dancers, acrobats, specialty performers)',
        'Interactive experiences and installations',
        'Social media-worthy moments designed throughout',
        'No idea too wild or ambitious'
      ],
      examples: [
        'Gatsby Glamour: 1920s speakeasy with prohibition-era cocktails',
        'Around the World: Each room features a different country\'s cuisine and culture',
        'Neon Wonderland: UV-reactive décor with glow-in-the-dark food and drinks',
        'Enchanted Forest: Outdoor fairy tale experience with whimsical forest cuisine',
        'Casino Royale: Monte Carlo-style gaming with upscale international fare',
        'Studio 54 Disco: 70s extravaganza with fondue fountains and disco balls'
      ]
    },
    {
      id: 'retirement',
      icon: <Heart size={48} />,
      title: 'Retirement Celebrations',
      tagline: 'Honoring a Lifetime of Achievement',
      color: 'from-[#ffd93d] to-[#f7b731]',
      image: 'https://customer-assets.emergentagent.com/job_jfrai-catering/artifacts/7zq0ux2w_image.png',
      description: 'Celebrate a well-deserved retirement with a party as special as the retiree. We create memorable events that honor careers, achievements, and the exciting journey ahead.',
      features: [
        'Elegant sit-down dinners or casual cocktail receptions',
        'Custom menu featuring retiree\'s favorite foods',
        'Timeline display setup (career milestones, photos)',
        'Tribute video coordination and screening',
        'Toast coordination and microphone setup',
        'Memory book station for guests to leave messages',
        'Gift table and display management',
        'Corporate or family-style celebrations'
      ],
      packages: [
        { name: 'Intimate Gathering', price: 'Starting at $2,000', includes: '25-50 guests, buffet or plated dinner' },
        { name: 'Grand Celebration', price: 'Starting at $5,000', includes: '50-100 guests, premium menu, full bar' },
        { name: 'Milestone Event', price: 'Starting at $10,000', includes: '100+ guests, luxury experience, entertainment, custom everything' }
      ]
    },
    {
      id: 'family-reunion',
      icon: <Users size={48} />,
      title: 'Family Reunions',
      tagline: 'Bringing Families Together',
      color: 'from-[#a8edea] to-[#fed6e3]',
      image: 'https://images.unsplash.com/photo-1528605248644-14dd04022da1?w=800&q=80',
      description: 'Reconnect with loved ones at a family reunion designed to create lasting memories. We handle all the logistics so you can focus on enjoying time with family.',
      features: [
        'Multi-generational menu planning (kid-friendly to senior preferences)',
        'Outdoor picnic setups or indoor banquet facilities',
        'Family-style serving or buffet options',
        'Activity coordination (games, sports, entertainment)',
        'Photo booth and professional photography',
        'Name tags and family tree displays',
        'Accommodation coordination for out-of-town guests',
        'Multi-day event planning available'
      ],
      packages: [
        { name: 'Backyard Bash', price: 'Starting at $1,500', includes: '30-60 people, casual BBQ or picnic style' },
        { name: 'Park Perfect', price: 'Starting at $3,000', includes: '60-150 people, park pavilion setup, complete catering' },
        { name: 'Resort Reunion', price: 'Starting at $8,000', includes: '150+ people, multi-day events, full coordination' }
      ]
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section 
        className="relative min-h-[60vh] flex items-center justify-center overflow-hidden" 
        data-testid="event-types-hero-section"
        style={{
          backgroundImage: 'linear-gradient(rgba(102, 126, 234, 0.85), rgba(118, 75, 162, 0.85)), url(https://images.unsplash.com/photo-1533174072545-7a4b6ad7a6c3?w=1600&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="hero-content fade-in-up relative z-10">
          <h1 className="hero-title text-white drop-shadow-lg" data-testid="event-types-title">
            Types of Events
          </h1>
          <div className="decorative-line bg-white"></div>
          <p className="hero-subtitle text-white drop-shadow-md" data-testid="event-types-subtitle">
            Extraordinary Experiences for Every Occasion
          </p>
        </div>
      </section>

      {/* Event Types Detailed Sections */}
      {eventTypes.map((event, index) => (
        <section 
          key={event.id}
          className="section"
          style={{ 
            background: index % 2 === 0 ? 'white' : 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)'
          }}
          data-testid={`event-type-${event.id}`}
        >
          <div className="max-w-7xl mx-auto">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              {/* Image Side */}
              <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                <div className="relative overflow-hidden rounded-2xl shadow-2xl group">
                  <img 
                    src={event.image}
                    alt={event.title}
                    className="w-full h-[400px] object-cover group-hover:scale-110 transition-transform duration-500"
                    style={event.id === 'retirement' ? { objectPosition: 'right center' } : {}}
                  />
                  <div className={`absolute inset-0 bg-gradient-to-br ${event.color} opacity-0 group-hover:opacity-30 transition-opacity duration-300`}></div>
                </div>
              </div>

              {/* Content Side */}
              <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br ${event.color} text-white mb-6 shadow-lg`}>
                  {event.icon}
                </div>
                
                <h2 className="text-4xl md:text-5xl font-bold text-[#8b4513] mb-3">
                  {event.title}
                </h2>
                <p className="text-xl text-[#d2691e] font-semibold mb-4 italic">
                  {event.tagline}
                </p>
                <div className="decorative-line" style={{ margin: '1rem 0' }}></div>
                
                <p className="text-lg text-[#6b4423] mb-6 leading-relaxed">
                  {event.description}
                </p>

                <h3 className="text-2xl font-bold text-[#8b4513] mb-4">What's Included:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                  {event.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start">
                      <span className="text-[#d2691e] mr-2 mt-1">✓</span>
                      <span className="text-[#6b4423]">{feature}</span>
                    </div>
                  ))}
                </div>

                {event.packages && (
                  <>
                    <h3 className="text-2xl font-bold text-[#8b4513] mb-4 mt-6">Packages:</h3>
                    <div className="space-y-4">
                      {event.packages.map((pkg, idx) => (
                        <div key={idx} className="card border-l-4 border-[#d2691e]">
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="text-lg font-bold text-[#8b4513]">{pkg.name}</h4>
                            <span className="text-xl font-bold text-[#d2691e]">{pkg.price}</span>
                          </div>
                          <p className="text-[#6b4423]">{pkg.includes}</p>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {event.examples && (
                  <>
                    <h3 className="text-2xl font-bold text-[#8b4513] mb-4 mt-6">Popular Themes:</h3>
                    <div className="space-y-2">
                      {event.examples.map((example, idx) => (
                        <div key={idx} className="flex items-start">
                          <span className="text-[#d2691e] mr-2">★</span>
                          <span className="text-[#6b4423]">{example}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* CTA Section */}
      <section 
        className="section text-center" 
        style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
        data-testid="event-types-cta-section"
      >
        <h2 className="section-title text-white">Ready to Plan Your Event?</h2>
        <div className="decorative-line bg-white"></div>
        <p className="section-subtitle text-white">
          Let's discuss how we can make your vision come to life. Contact us for a personalized quote.
        </p>
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          <Link to="/contact">
            <button className="btn-primary bg-white text-[#667eea] hover:bg-[#ffecd2]" data-testid="event-types-contact-btn">
              Get a Quote
            </button>
          </Link>
          <Link to="/events">
            <button className="btn-secondary bg-transparent text-white border-white hover:bg-white hover:text-[#667eea]" data-testid="event-types-planning-btn">
              View Event Planning Services
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default EventTypes;
